import { DateTime } from 'luxon';
import { BREAKPOINTS } from './breakpoints';
import { Field, RouteData, Item } from '@sitecore-jss/sitecore-jss-nextjs';
import { SessionStorageService } from '../../stores';
import { GtmDataLayer, GtmDimensions } from 'google-tag-manager';
import { FunnelQueryCookieObject, cookieNames, readCookie } from '../../cookie-utils';
import { fetchAuthenticationStatus } from '../../apis/bff/methods/search/helpers';
import { AuthResponse } from 'server-schemas/channels';
import { Config } from 'server-schemas';
import { fromSearchCookieString } from '../../cookie-utils/search/utils';
import { Bundle } from 'server-schemas/channels/basket';
import { bundleDiscountAdjustmentCode } from '../../apis/bff/methods/bundles/constants';
declare global {
  interface Window {
    gtmDataLayer?: GtmDataLayer[];
  }
}
let version: Config.Version | string = { majorVersion: '1', minorVersion: '0', patchVersion: 'dev' };
let environment: string = '';

export const ANALYTICS_AUTHENTICATION_VALUE = 'analyticsAuthenticationValue';

export const hasReferrerSameDomain = (): boolean => {
  const referralPageUrl = getPageReferral();
  return referralPageUrl !== '' && referralPageUrl.indexOf(window.location.hostname) !== -1;
};

export const getPageNameReferrer = (): string => {
  if (hasReferrerSameDomain()) {
    return SessionStorageService.getItem('referralPageName') || '';
  }
  return '';
};

export const getReferralPageName = (): string => {
  if (hasReferrerSameDomain()) {
    return SessionStorageService.getItem(GtmDimensions.referralPageName) || '';
  }
  return '';
};

export const getPageCategoryReferrer = (): string => {
  if (hasReferrerSameDomain()) {
    return SessionStorageService.getItem('referralPageCategory') || '';
  }
  return '';
};

export const getReferralPageCategory = (): string => {
  if (hasReferrerSameDomain()) {
    return SessionStorageService.getItem(GtmDimensions.referralPageCategory) || '';
  }
  return '';
};

export const removeAnalyticsAuthenticationValue = () => {
  sessionStorage.removeItem(ANALYTICS_AUTHENTICATION_VALUE);
};

export const getAuthenticationStatus = async (): Promise<string> => {
  const analyticsAuthenticationValue = sessionStorage.getItem(ANALYTICS_AUTHENTICATION_VALUE);
  if (analyticsAuthenticationValue) return analyticsAuthenticationValue;
  try {
    const authResponse: AuthResponse = await fetchAuthenticationStatus();
    if (authResponse.isAuthenticated) {
      sessionStorage.setItem(ANALYTICS_AUTHENTICATION_VALUE, 'Yes');
      return 'Yes';
    }
    sessionStorage.setItem(ANALYTICS_AUTHENTICATION_VALUE, 'No');
    return 'No';
  } catch (error) {
    // A timeout error from fetchBff should not prevent the other anayltics data from being sent
    sessionStorage.setItem(ANALYTICS_AUTHENTICATION_VALUE, 'No');
    return 'No';
  }
};

export const getComponentSenderFromCookieIfAvailable = (flightQuery: FunnelQueryCookieObject): string => {
  // This function reads from the FunnelQuery cookie property called `Snd`.
  // example local: "Snd": "SearchPod2_/de/"
  // example external: "Snd": "google"
  return flightQuery.componentSender || '';
};

export const setPageCategory = (pageCategory: string) => {
  SessionStorageService.setItem(GtmDimensions.pageCategory, pageCategory);
};

export const getPageCategoryFromSitecore = (route: RouteData<Record<string, Field | Item | Item[]>>): string => {
  return String((route?.fields?.pageCategory as Field)?.value) || '';
};

export const getPageCategoryFromSessionStorage = (): string => {
  return SessionStorageService.getItem(GtmDimensions.pageCategory) || '';
};

export const setLangCode = (pageLang: string) => {
  SessionStorageService.setItem(GtmDimensions.pageLanguage, pageLang.toUpperCase());
};

export const getLangCode = (): string => {
  return SessionStorageService.getItem(GtmDimensions.pageLanguage) || '';
};

export const getLoggedInStatus = (): string => {
  const value = sessionStorage.getItem(ANALYTICS_AUTHENTICATION_VALUE);
  return value && value.trim() ? value : 'No';
};

export const setPageName = (pageName: string) => {
  SessionStorageService.setItem(GtmDimensions.pageName, pageName);
};

export const getPageNameFromSitecore = (route: RouteData<Record<string, Field | Item | Item[]>>): string => {
  const lang = getLangCode();
  const title = String((route?.fields?.pageName as Field)?.value) || '';
  return `${title} | ${lang}`;
};

export const getPageNameFromSessionStorage = (): string => {
  return SessionStorageService.getItem(GtmDimensions.pageName) || '';
};

export const getPageReferral = (): string => {
  return document.referrer || '';
};

export const getEnvironmentName = (): string => {
  return environment;
};

export const getCurrentPageUrl = (): string => {
  return window.location.href;
};

export const getScreenOrientation = (): string => {
  return window.innerWidth > window.innerHeight ? 'Landscape' : 'Portrait';
};

export const getTimeStamp = (): string => {
  return DateTime.now().toFormat('yyyy-MM-dd_HH:mm:ss');
};

export const setDataFromServerSide = ({
  version: v,
  environment: e
}: {
  version: Config.Version | string;
  environment: string;
}) => {
  version = v;
  environment = e;
};

export const getVersionNumber = (): string => {
  let versionValue = version.toString();
  if (typeof version === 'object') {
    const { majorVersion, minorVersion, patchVersion } = version;
    versionValue = `${majorVersion}.${minorVersion}.${patchVersion}`;
  }
  return `MA|v${versionValue}`;
};

export const getBreakpointName = (): string => {
  const actualWindowSize = window.innerWidth;
  const isMobile = (): boolean => {
    return actualWindowSize < BREAKPOINTS.TABLET.MIN;
  };
  const isTablet = (): boolean => {
    return actualWindowSize < BREAKPOINTS.DESKTOP.MIN;
  };
  const isDesktop = (): boolean => {
    return actualWindowSize < BREAKPOINTS.LARGE.MIN;
  };
  if (isMobile()) {
    return 'Mobile';
  } else if (isTablet()) {
    return 'Tablet';
  } else if (isDesktop()) {
    return 'Desktop';
  } else {
    return 'Wide';
  }
};

export const getFlightsRoute = (query: FunnelQueryCookieObject) => {
  if (!query.isOneWay) {
    return `${query.originIata}-${query.destinationIata}|${query.destinationIata}-${query.originIata}`;
  }

  return `${query.originIata}-${query.destinationIata}`;
};

export const getDaysToDeparture = (outboundDate: string) => {
  return DateTime.fromFormat(outboundDate, 'y-MM-dd').diff(DateTime.now().startOf('day'), 'days').days;
};

export const groupImpressionsByProperty = (objectArray: any[], property: string) => {
  const groupedFlights: Record<string, any[]> = {};

  objectArray.forEach(obj => {
    const key = obj[property];
    if (!(key in groupedFlights)) {
      groupedFlights[key] = [];
    }
    groupedFlights[key].push(obj);
  });

  return Object.values(groupedFlights).flat();
};

export const getReturnDateFromSearchCookie = (): string => {
  const searchCookieString = readCookie(cookieNames.Search.lastSearch.name) ?? '';
  const returnDate = fromSearchCookieString(searchCookieString)?.returnDate?.valueOf() as string;
  return returnDate?.split('T')[0] ?? '';
};

export const setReferralPages = (pageName: string, pageCategory: string) => {
  const referralPageName = SessionStorageService.getItem(GtmDimensions.referralPageName);
  const referralPageCategory = SessionStorageService.getItem(GtmDimensions.referralPageCategory);
  if (referralPageName && referralPageCategory) {
    SessionStorageService.setItem('referralPageName', referralPageName);
    SessionStorageService.setItem('referralPageCategory', referralPageCategory);
  }
  SessionStorageService.setItem(GtmDimensions.referralPageName, pageName);
  SessionStorageService.setItem(GtmDimensions.referralPageCategory, pageCategory);
};

export const pushToDataLayer = (data: GtmDataLayer) => {
  if (typeof window !== 'undefined') {
    window.gtmDataLayer = window.gtmDataLayer || [];
    window.gtmDataLayer.push(data);
  }
};

/**
 * Calculates the difference between 2 prices in the form promotionalPrice - standardPrice = x
 * @example
 * // returns -10.50
 * itemPriceDifference(10, 20.50)
 * @example
 * // returns -10
 * itemPriceDifference(10, 20)
 * @example
 * // returns "NULL"
 * itemPriceDifference(); itemPriceDifference(undefined, undefined); itemPriceDifference(10, undefined);
 * @returns {Number} Returns the value of x for the calculation.
 */
export const itemPriceDifference = (promotionalPrice?: number, standardPrice?: number) => {
  let itemPriceDifference: number | string | null =
    promotionalPrice && standardPrice ? (promotionalPrice - standardPrice).toFixed(2) : null;

  itemPriceDifference =
    itemPriceDifference?.toString().split('.')[1] === '00' ? +itemPriceDifference?.toString().split('.')[0] : itemPriceDifference;

  return itemPriceDifference;
};

export const isDiscounted = (bundle: Bundle) =>
  bundle.price.adjustments?.some(adjustment => adjustment.code === bundleDiscountAdjustmentCode) ?? false;
